import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { Button, Alert } from '@trussworks/react-uswds';

import StepHeader from '@components/step-header/StepHeader';
import DatePicker from '@components/date-picker/DatePicker';
import TextInput from '@components/text-input/TextInput';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Section from '@components/section/Section';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { filterNullEmptyObjects } from '@src/utils/helpers';
import { useRequest, useUserRoleCapabilities } from '@stores';
import { useUserProfile, useResetDirtyFields } from '@hooks';
import { useLookupTableFormVersions } from '@api/services/lookup/hooks';
import { RequestType } from '@src/utils/enums';
import CertifySignSubmitRoleAlert from '@forms/input-forms/common/CertifySignSubmitRoleAlert';

export const ViolationsFormCertifySignSubmitMetadata = {
  sectionName: 'Certify, Sign, and Submit',
};

const doClearIncompletePhoneInfoVCOnly = (payload) => {
  const contacts = payload?.request?.violationComplaints?.[0]?.contacts;

  const filteredContacts = contacts?.map((contact) => {
    let updatedContact = { ...contact };

    // Check phoneNumberOne and remove country code if empty
    if (!contact?.phoneOne || contact?.phoneOne?.trim() === '') {
      updatedContact.phoneOneCountryCode = '';
    }

    // Check phoneNumberTwo and remove country code if empty
    if (!contact?.phoneTwo || contact?.phoneTwo?.trim() === '') {
      updatedContact.phoneTwoCountryCode = '';
    }

    // Check faxPhone and remove country code if empty
    if (!contact?.faxPhone || contact?.faxPhone?.trim() === '') {
      updatedContact.faxCountryCode = '';
    }

    return updatedContact;
  });

  return {
    ...payload,
    request: {
      ...payload?.request,
      violationComplaints: [
        {
          ...payload?.request?.violationComplaints?.[0],
          contacts: filteredContacts,
        },
      ],
    },
  };
};

const ViolationsFormCertifySignSubmit = connect(
  'doSubmitAnonymous',
  'doUpdateRequestObject',
  'doUploadAndUpdateViolationComplaint',
  'selectIsAnonymous',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectViolationComplaintErrors',
  ({
    doSubmitAnonymous,
    doUpdateRequestObject,
    doUploadAndUpdateViolationComplaint,
    isAnonymous,
    requestAPIData,
    requestFormData,
    violationComplaintErrors,
    stepNo,
  }) => {
    const { userProfileData } = useUserProfile();
    const canUserSign = useUserRoleCapabilities((state) => state.roleCapabilities.signSubmitRequest);
    const lookupTableFormVersions = useLookupTableFormVersions();
    const { isFormReadOnly: isReadOnly, updateSection, sections, isSectionTouched } = useRequest();
    const formVersion = lookupTableFormVersions.find(
      (item) => item.requestType === RequestType.ViolationComplaint
    )?.formVersion;
    const canSignRequest = isAnonymous || canUserSign;

    // Signature always validated against user profile name
    const getName = useCallback(
      () =>
        userProfileData.middleName
          ? `${userProfileData.firstName} ${userProfileData.middleName} ${userProfileData.lastName}`
          : `${userProfileData?.firstName} ${userProfileData.lastName}`,
      [userProfileData?.firstName, userProfileData?.middleName, userProfileData?.lastName]
    );

    const defaultValues = {
      anonymous: isAnonymous,
      signature: requestAPIData?.request?.signature ?? (isAnonymous ? 'Anonymous' : null),
      signatureDate:
        requestAPIData?.request?.signatureDate !== null
          ? requestAPIData?.request?.signatureDate?.split('T')[0]
          : ('' ?? ''),
    };

    const [isSigned, setIsSigned] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const schema = yup.object().shape({
      anonymous: yup.boolean(),
      signature: yup
        .string()
        .required('Signature is required')
        .when('anonymous', {
          is: true || null,
          then: () => yup.string().test('isAnon', 'Signature must be "Anonymous"', (val) => val === 'Anonymous'),
        })
        .when('anonymous', {
          is: false,
          then: () =>
            yup.string().test('isSigned', `Signature must be ${getName()}`, (val) => val.trim() === getName()),
        }),
      signatureDate: yup.string().required('Please select a date'),
    });

    const methods = useForm({ defaultValues: defaultValues, resolver: yupResolver(schema), mode: 'onBlur' });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      handleSubmit,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const isTouched = isSectionTouched(stepNo);
    const errorCount = Object.values(errors)?.length;
    const anonymous = watch('anonymous');
    const signature = watch('signature');
    const signatureDate = watch('signatureDate');
    const dateNow = new Date().toISOString().slice(0, 10);

    //Reset the dirty states of the fields after a save.
    !isAnonymous && useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const handleBlur = (e) => {
      setValue('signature', e.target.value.trim());
    };

    useEffect(() => {
      if ((anonymous && signature === 'Anonymous') || (!anonymous && signature?.trim() === getName())) {
        !signatureDate && setValue('signatureDate', new Date().toISOString().slice(0, 10));
        setIsSigned(true);
        trigger();
      } else {
        setIsSigned(false);
      }
    }, [signature, anonymous, getName, setValue, signatureDate, trigger]);

    const submitForm = () => {
      const requestFormDataSubmit = {
        ...requestFormData,
        request: { ...requestFormData.request, isSubmit: true, formVersion: formVersion },
        location: {
          ...requestFormData.location,
          projectArea: !requestFormData?.location?.projectArea ? 0 : (requestFormData.location.projectArea ?? 0),
        },
      };
      const cleanedPayload = doClearIncompletePhoneInfoVCOnly(requestFormDataSubmit);
      const filteredRequest = filterNullEmptyObjects(cleanedPayload);
      anonymous ? doSubmitAnonymous(filteredRequest) : doUploadAndUpdateViolationComplaint(filteredRequest, 'Submit');
    };

    // Consolidated effect: Calculate form validity and update section state
    useEffect(() => {
      const hasInvalidSections = sections.some(
        (section) => section.valid === false && section.label !== 'Certify, Sign, and Submit'
      );
      const newIsFormValid = !hasInvalidSections;

      // Only update if state actually changed to prevent cascading re-renders
      if (newIsFormValid !== isFormValid) {
        setIsFormValid(newIsFormValid);
      }

      // Update section with all props at once
      updateSection(stepNo, {
        dirty: isDirty,
        valid: newIsFormValid && isSigned,
      });

      // Only update request on successful completion
      if (newIsFormValid && isSigned && signature && signatureDate) {
        doUpdateRequestObject({ signature, signatureDate });
      }

      // Trigger form validation
      trigger();
    }, [
      stepNo,
      isDirty,
      isFormValid,
      isSigned,
      signature,
      signatureDate,
      sections,
      doUpdateRequestObject,
      updateSection,
      trigger,
    ]);

    const renderSignatureSection = () => {
      if (!isFormValid) {
        return (
          <Alert slim className='margin-top-4' type='error'>
            In order to submit this form, please revisit the section(s) marked with errors, and correct any missing or
            invalid fields.
          </Alert>
        );
      }

      return (
        <Section label='Signature'>
          <div className='grid-row grid-gap-1'>
            <div className='grid-col-6'>
              <TextInput
                name='signature'
                label='Signature'
                signature={isAnonymous ? 'Anonymous' : getName()}
                required
                readOnly={isReadOnly || !canSignRequest}
                onBlur={handleBlur}
              />
            </div>
            <div className='grid-col-6'>
              <DatePicker
                min={null}
                max={dateNow}
                name='signatureDate'
                label='Signature Date'
                readOnly={isReadOnly || !canSignRequest}
                required
              />
            </div>
          </div>
          <hr />
          <div className='display-flex flex-justify-start padding-top-1'>
            <Button
              className='submit-request-button'
              title='Submit Request'
              disabled={!isSigned || isReadOnly || !canSignRequest}
              onClick={handleSubmit(submitForm)}
            >
              Submit Request
            </Button>
          </div>
        </Section>
      );
    };

    return (
      <FormProvider {...methods}>
        {/* Error Summary list for section errors */}
        {(!isFormValid || errorCount > 0) && !isReadOnly && <ErrorSummary errors={errors} type='form' />}
        {/* Error Summary list for validation errors */}
        {violationComplaintErrors && Object.keys(violationComplaintErrors)?.length > 0 && (
          <ErrorSummary errors={violationComplaintErrors} type='api-validation' />
        )}
        {!canSignRequest && <CertifySignSubmitRoleAlert showAlert={!canSignRequest} />}
        <StepHeader text='Certify, Sign, Submit' />

        <h6>BY SUBMITTING THIS APPLICATION, I AM AGREEING TO ALL OF THE FOLLOWING:</h6>
        <ul className='padding-left-3 text-bold'>
          <li>
            I consent to conduct business electronically and understand this consent can be withdrawn at any time in
            writing to the local USACE Regulatory office.
          </li>
          <li>I certify that the information in this report is complete and accurate to the best of my knowledge.</li>
          <li>
            I understand that, whoever, in any matter within the jurisdiction of any department of agency of the United
            States, knowingly and willfully (1) falsifies, conceals, or covers up by any trick, scheme, or device a
            material fact; (2) makes any materially false, fictitious, or fraudulent statement or representation; or (3)
            makes or uses any false writing or document knowing the same to contain any materially false, fictitious, or
            fraudulent statement or entry; shall be fined not more than $10,000 or imprisoned not more than 5 years or
            both. (18 USC Section 1001)
          </li>
          <li>
            I understand that by typing my name or Anonymous in the text field below that I am e-signing this
            application and I understand that this e-signature has the same legal status as handwritten signatures.
          </li>
        </ul>
        {renderSignatureSection()}
      </FormProvider>
    );
  }
);
ViolationsFormCertifySignSubmit.metadata = ViolationsFormCertifySignSubmitMetadata;

export default ViolationsFormCertifySignSubmit;
